<template lang="pug">
  dv-text-field(
    v-maska:[extraMaskaOptions]
    v-bind="$attrs"
    v-on="inputListeners"
    :value="outputValue"
    @input="onInput"
    @blur="onBlur"
  )
    template(
      v-for="(_, name) in $scopedSlots"
      v-slot:[name]="data"
    )
      slot(:name="name" v-bind="data")
    template(
      v-for="(_, name) in $slots"
      :slot="name"
    )
      slot(:name="name")
</template>

<script>
import { vMaska } from 'maska';
import DvTextField from './DvTextField.vue';

const DEFAULT_MASKA_OPTIONS = {
  mask: undefined,
  eager: false,
  tokens: {},
  tokensReplace: {},
  reversed: false,
  preProcess: (v) => v,
  postProcess: (v) => v,
  serializer: (v) => v,
  returnMasked: false,
};

export default {
  directives: {
    maska: vMaska,
  },
  components: {
    DvTextField,
  },
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    updateOn: {
      type: String,
      default: 'blur',
      validator: (value) => ['input', 'blur'].includes(value),
    },
    maskaOptions: {
      type: Object,
      default: () => ({}),
    },
    value: {
      type: [String, Number],
      default: undefined,
    },
  },
  data() {
    return {
      outputValue: this.value,
      lastEmittedValue: this.value,
    };
  },
  watch: {
    value(value) {
      this.lastEmittedValue = value;
      // A model echo must not erase editable drafts such as '-' or '1.'.
      if (!Object.is(value, this.serializedValue)) this.outputValue = value;
    },
  },
  computed: {
    inputListeners() {
      const { input, blur, ...listeners } = this.$listeners;
      return listeners;
    },
    extraMaskaOptions() {
      const maskaOptions = {
        ...DEFAULT_MASKA_OPTIONS,
        ...this.maskaOptions,
      };
      const { returnMasked, postProcess, onMaska } = maskaOptions;

      return {
        ...maskaOptions,
        onMaska: (detail) => {
          const { masked, unmasked } = detail;
          const outputValue = returnMasked ? masked : unmasked;

          this.outputValue = postProcess(outputValue);
          if (this.updateOn === 'input') this.emitValue();

          if (onMaska) {
            const callbacks = Array.isArray(onMaska) ? onMaska : [onMaska];
            callbacks.forEach((callback) => callback(detail));
          }
        },
      };
    },
    serializedValue() {
      const { outputValue } = this;
      const { serializer } = this.extraMaskaOptions;

      return serializer(outputValue);
    },
  },
  methods: {
    emitValue(force = false) {
      const value = this.serializedValue;
      if (force || !Object.is(value, this.lastEmittedValue)) {
        this.lastEmittedValue = value;
        this.$emit('input', value);
      }
    },
    onInput(value) {
      // Vuetify's clear button emits null without a native masked input event.
      if (value == null) {
        this.outputValue = '';
        if (this.updateOn === 'input') this.emitValue();
      }
    },
    onBlur(event) {
      this.emitValue(this.updateOn === 'blur');
      this.outputValue = this.serializedValue;
      this.$emit('blur', event);
    },
  },
};
</script>
